import React, { forwardRef } from 'react';
import cn from 'bem-cn';

import { useClickOutside } from '../../../hooks';
import { Icon } from '../Icon';
import { Stack } from '../Stack';

import { DropdownItem, DropdownProps } from './Dropdown.types';
import { useDropdown } from './Dropdown.hooks';

import './Dropdown.scss';

const b = cn('dropdown');

const DropdownComponent = <T extends string>(
    {
        items,
        trigger,
        onSelect,
        multiply = false,
        selectedValues = [],
        placement = 'bottom-start',
        className,
        triggerClassName,
        disabled = false,
        maxHeight = 500,
        autoCloseOnTriggerHidden = false,
        ...props
    }: DropdownProps<T>,
    ref: React.Ref<HTMLDivElement>,
) => {
    const { isOpen, dropdownRef, triggerRef, handleToggle, handleSelect, setIsOpen, isItemSelected } = useDropdown({
        items,
        onSelect,
        multiply,
        selectedValues,
        disabled,
        autoCloseOnTriggerHidden,
    });

    const handleClose = () => {
        setIsOpen(false);
    };

    useClickOutside({
        ref: dropdownRef,
        isActive: isOpen,
        onClickOutside: handleClose,
    });

    const dropdownClassName = b({
        open: isOpen,
        disabled,
        multiply,
    }).mix(className);

    const renderItem = (item: DropdownItem<T>) => {
        const isSelected = isItemSelected(item);

        return (
            <Stack
                key={item.id}
                direction="row"
                className={b('item', {
                    disabled: item.disabled,
                    selected: isSelected,
                })}
                onClick={() => handleSelect(item)}
            >
                <span className={b('item', 'label')}>{item.label}</span>
                <div className={b('item', 'checkbox', { multiply, checked: isSelected })}>
                    {isSelected && (
                        <Icon name="checkboxTick" className={b('checkbox-icon')} strokeColor="#fff" size={12} />
                    )}
                </div>
            </Stack>
        );
    };

    const renderItems = () => {
        return items.map(renderItem);
    };

    return (
        <Stack ref={ref || dropdownRef} className={dropdownClassName} {...props}>
            <button
                ref={triggerRef}
                className={b('trigger', { disabled }).mix(triggerClassName)}
                onClick={handleToggle}
                disabled={disabled}
            >
                <span className={b('trigger', 'text')}>{trigger}</span>
                <Icon name="chevronRight" className={b('trigger', 'icon')} size={24} rotate={isOpen ? -90 : 90} />
            </button>

            <Stack className={b('content', { placement, opened: isOpen })} style={{ maxHeight }}>
                {renderItems()}
            </Stack>
        </Stack>
    );
};

export const Dropdown = forwardRef(DropdownComponent) as <T extends string>(
    props: DropdownProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
